import React from 'react';
import { Save, X } from 'lucide-react';

const ModalShell = ({ title, kicker, onClose, children, testId, t }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-950/60 p-4"
    role="dialog"
    aria-modal="true"
    data-testid={testId}
  >
    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          {kicker ? (
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{kicker}</p>
          ) : null}
          <h3 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{title}</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.cancel')}
          data-testid={`${testId}-close`}
          className="inline-flex min-h-11 w-11 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
        >
          <X size={20} strokeWidth={1.8} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const Actions = ({ onCancel, t, submitTestId }) => (
  <div className="flex gap-3 pt-2">
    <button
      type="button"
      onClick={onCancel}
      className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50"
      data-testid="modal-cancel"
    >
      {t('common.cancel')}
    </button>
    <button
      type="submit"
      className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
      data-testid={submitTestId}
    >
      <Save size={16} strokeWidth={1.8} />
      {t('common.save')}
    </button>
  </div>
);

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-bold text-zinc-700">
      {label}{required ? ' *' : ''}
    </span>
    {children}
  </label>
);

const inputClass = 'min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100';
const textareaClass = 'w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100';

export const ActivityModal = ({ draft, setDraft, onSubmit, onClose, t }) => (
  <ModalShell title={t('fitness.newActivity')} kicker={t('fitness.tabActivity')} onClose={onClose} testId="activity-modal" t={t}>
    <form onSubmit={onSubmit} className="space-y-4">
      <Field label={t('common.type')}>
        <select
          className={inputClass}
          value={draft.activity_type}
          onChange={(e) => setDraft({ ...draft, activity_type: e.target.value })}
          data-testid="activity-input-type"
        >
          {['running', 'cycling', 'gym', 'stretching', 'swimming', 'other'].map((key) => (
            <option key={key} value={key}>{t(`fitness.activityTypes.${key}`, key)}</option>
          ))}
        </select>
      </Field>
      <Field label={t('common.date')}>
        <input className={inputClass} type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} required data-testid="activity-input-date" />
      </Field>
      <Field label={`${t('common.duration')} (${t('common.min')})`}>
        <input className={inputClass} type="number" min="1" value={draft.duration} onChange={(e) => setDraft({ ...draft, duration: parseInt(e.target.value, 10) || 0 })} required data-testid="activity-input-duration" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t('fitness.calories')}>
          <input className={inputClass} type="number" value={draft.calories} onChange={(e) => setDraft({ ...draft, calories: e.target.value })} data-testid="activity-input-calories" />
        </Field>
        <Field label={`${t('fitness.distance')} (km)`}>
          <input className={inputClass} type="number" step="0.1" value={draft.distance} onChange={(e) => setDraft({ ...draft, distance: e.target.value })} data-testid="activity-input-distance" />
        </Field>
      </div>
      <Field label={`${t('fitness.heartRate')} (${t('fitness.heartRateUnit')})`}>
        <input className={inputClass} type="number" value={draft.avg_heart_rate} onChange={(e) => setDraft({ ...draft, avg_heart_rate: e.target.value })} data-testid="activity-input-hr" />
      </Field>
      <Field label={t('common.notes')}>
        <textarea className={textareaClass} rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="activity-input-notes" />
      </Field>
      <Actions onCancel={onClose} t={t} submitTestId="activity-modal-save" />
    </form>
  </ModalShell>
);

const RangeRow = ({ label, value, onChange, testId, tone = 'lime' }) => (
  <div className="rounded-lg bg-zinc-50 p-3">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-bold text-zinc-700">{label}</span>
      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-black text-zinc-950 ring-1 ring-zinc-200">{value}</span>
    </div>
    <input
      type="range"
      min="1"
      max="5"
      value={value}
      onChange={onChange}
      className={`w-full ${tone === 'rose' ? 'accent-rose-500' : tone === 'emerald' ? 'accent-emerald-500' : 'accent-lime-500'}`}
      data-testid={testId}
    />
  </div>
);

export const RecoveryModal = ({ draft, setDraft, onSubmit, onClose, t }) => (
  <ModalShell title={t('fitness.newRecovery')} kicker={t('fitness.tabRecovery')} onClose={onClose} testId="recovery-modal" t={t}>
    <form onSubmit={onSubmit} className="space-y-4">
      <Field label={t('common.date')}>
        <input className={inputClass} type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} required data-testid="recovery-input-date" />
      </Field>
      <Field label={t('fitness.sleepHours')}>
        <input className={inputClass} type="number" step="0.5" value={draft.sleep_hours} onChange={(e) => setDraft({ ...draft, sleep_hours: parseFloat(e.target.value) || 0 })} data-testid="recovery-input-sleep" />
      </Field>
      <RangeRow label={t('fitness.sleepQuality')} value={draft.sleep_quality} onChange={(e) => setDraft({ ...draft, sleep_quality: parseInt(e.target.value, 10) })} testId="recovery-input-quality" tone="emerald" />
      <RangeRow label={t('fitness.fatigue')} value={draft.fatigue_level} onChange={(e) => setDraft({ ...draft, fatigue_level: parseInt(e.target.value, 10) })} testId="recovery-input-fatigue" tone="rose" />
      <RangeRow label={t('fitness.soreness')} value={draft.muscle_soreness} onChange={(e) => setDraft({ ...draft, muscle_soreness: parseInt(e.target.value, 10) })} testId="recovery-input-soreness" tone="rose" />
      <RangeRow label={t('fitness.stress')} value={draft.stress_level} onChange={(e) => setDraft({ ...draft, stress_level: parseInt(e.target.value, 10) })} testId="recovery-input-stress" tone="rose" />
      <Field label={t('common.notes')}>
        <textarea className={textareaClass} rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="recovery-input-notes" />
      </Field>
      <Actions onCancel={onClose} t={t} submitTestId="recovery-modal-save" />
    </form>
  </ModalShell>
);

export const InjuryModal = ({ draft, setDraft, onSubmit, onClose, t }) => (
  <ModalShell title={t('fitness.newInjury')} kicker={t('fitness.tabInjuries')} onClose={onClose} testId="injury-modal" t={t}>
    <form onSubmit={onSubmit} className="space-y-4">
      <Field label={t('fitness.injuryType')} required>
        <input className={inputClass} type="text" value={draft.injury_type} onChange={(e) => setDraft({ ...draft, injury_type: e.target.value })} required data-testid="injury-input-type" />
      </Field>
      <Field label={t('fitness.bodyPart')} required>
        <input className={inputClass} type="text" value={draft.body_part} onChange={(e) => setDraft({ ...draft, body_part: e.target.value })} required data-testid="injury-input-body" />
      </Field>
      <Field label={t('fitness.severityLabel')}>
        <select className={inputClass} value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.target.value })} data-testid="injury-input-severity">
          {['minor', 'moderate', 'severe'].map((key) => (
            <option key={key} value={key}>{t(`fitness.severities.${key}`, key)}</option>
          ))}
        </select>
      </Field>
      <Field label={t('common.date')}>
        <input className={inputClass} type="date" value={draft.date_occurred} onChange={(e) => setDraft({ ...draft, date_occurred: e.target.value })} required data-testid="injury-input-date" />
      </Field>
      <Field label={t('fitness.treatment')}>
        <textarea className={textareaClass} rows={2} value={draft.treatment} onChange={(e) => setDraft({ ...draft, treatment: e.target.value })} data-testid="injury-input-treatment" />
      </Field>
      <Field label={t('common.notes')}>
        <textarea className={textareaClass} rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="injury-input-notes" />
      </Field>
      <Actions onCancel={onClose} t={t} submitTestId="injury-modal-save" />
    </form>
  </ModalShell>
);
